import React, { Component, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { isEmpty, omit } from 'lodash';
import {
  seatingDetailsErrorStrings,
  seatingDetailsRules,
  seatingDetailsRedesignErrorStrings,
  seatingDetailsRedesignRules,
} from 'shared/validations';
import { ValidationService } from 'shared/services';
import { Input, ShapeSelect } from 'shared/components';
import {
  SPACING_FT_BY_SEATING_KEY,
  SHAPE_ICON_KEYS_BY_SEATING_KEY,
  transformOptions,
  resolveSeating,
} from 'shared/seatingArrangement';
import './SeatingDetails.scss';

const seatingDetails = {
  seatingRowCount: 0,
  seatingColumnCount: 0,
  writingSurfaceWidth: 0,
  writingSurfaceDepth: 0,
  distanceBetweenShoulders: 0,
  distanceBetweenHeads: 0,
};

class SeatingDetails extends Component {
  constructor(props) {
    super(props);

    // Set up the ref if provided
    if (props.forwardedRef) {
      props.forwardedRef.current = this;
    }

    const options = { seating: transformOptions(props.seatingOptions || []) };

    this.state = {
      room: Object.assign({}, seatingDetails, props.room),
      // Only meaningful when redesignEnabled — the room_setup_redesign_enabled flag-off
      // path (legacy RoomSeatingDetails.jsx wizard step) never reads/writes this state.
      seatingArrangement: props.redesignEnabled
        ? resolveSeating(options.seating, props.room.seating) || this.getDefaultSeating(options) || {}
        : {},
      options,
      isDirty: false,
      errors: {},
    };

    this.onSeatingDetailsChange = this.onSeatingDetailsChange.bind(this);
    this.onSelectSeatingArrangement = this.onSelectSeatingArrangement.bind(this);
  }

  componentDidMount() {
    this.notifyValidityChange();
  }

  componentDidUpdate(prevProps) {
    if (
      !this.state.room.id &&
      !this.props.room.loading &&
      !isEmpty(this.props.room) &&
      prevProps.room !== this.props.room
    ) {
      this.setState({
        room: this.props.room,
      });
    }

    if (
      this.props.redesignEnabled &&
      (prevProps.room !== this.props.room ||
        prevProps.seatingOptions !== this.props.seatingOptions ||
        // ModalSeatingDetails fetches the redesign flag (configValues) and the seating
        // options (roomOptions.seating) via two independent async calls. If the flag
        // resolves last, redesignEnabled can flip false -> true on a render where
        // room/seatingOptions haven't themselves changed since the previous render — the
        // two conditions above alone would never re-resolve seatingArrangement in that
        // case, leaving it stuck at the {} the constructor computed while redesignEnabled
        // was still false, with no tile ever shown as selected.
        prevProps.redesignEnabled !== this.props.redesignEnabled)
    ) {
      const options = { seating: transformOptions(this.props.seatingOptions || []) };
      this.setState({
        options,
        seatingArrangement:
          resolveSeating(options.seating, this.props.room.seating) ||
          this.getDefaultSeating(options) ||
          {},
      });
    }

    // Unconditional (not folded into either branch above) so this always reflects the
    // freshest committed state, regardless of which setState call (if any) triggered this
    // update — including plain field edits (onSeatingDetailsChange/
    // onSelectSeatingArrangement), which don't otherwise hit either guard above.
    this.notifyValidityChange();
  }

  // STATEDIST-11666 follow-up: seatingDetailsRedesignRules (shared/validations.js) requires
  // seat count unconditionally — Save silently did nothing if it was blank (onSubmit's own
  // validation returns undefined, so ModalSeatingDetails never calls updateRoom). Same
  // "looks clickable but does nothing" problem STATEDIST-11673 fixed for RoomStructure.jsx's
  // Continue button by disabling it outright instead of leaving it silently inert. Mirrors
  // that here. Only meaningful when redesignEnabled and required — the legacy flag-off
  // popup's own silent-no-op-on-invalid-submit behavior is unchanged.
  isDisabled() {
    if (!this.props.redesignEnabled || !this.props.required) {
      return false;
    }
    const { room, seatingArrangement, errors, seatsError } = this.state;
    const seatCountValid = room.seatingRowCount > 0 && room.seatingColumnCount > 0;
    const hasFieldErrors = Object.keys(errors).some((key) => errors[key]);
    return isEmpty(seatingArrangement) || !seatCountValid || hasFieldErrors || !!seatsError;
  }

  // STATEDIST-11666 follow-up: pushes the current disabled state up to ModalSeatingDetails
  // (via the optional onValidityChange prop) so it can feed ModalInfo's disabledSubmit —
  // this component's own internal state changes on every keystroke, which the parent has no
  // other way to observe (it only reaches this component's data imperatively, via the ref,
  // at Save-click time). A no-op when the prop isn't supplied (e.g. existing tests/consumers
  // that don't care).
  notifyValidityChange() {
    if (this.props.onValidityChange) {
      this.props.onValidityChange(this.isDisabled());
    }
  }

  // Hoisted out of the constructor (and reused by componentDidUpdate), mirroring
  // RoomStructure.jsx's (STATEDIST-11172) same fallback so both consumers of this
  // shared "Rows & columns" default stay in sync.
  getDefaultSeating(options = this.state.options) {
    return options.seating.find((s) => s.key === 'rowsColumns');
  }

  onSubmit() {
    const { room, seatingArrangement } = this.state;
    const { redesignEnabled } = this.props;
    let errors = {};
    let seatsError;

    const rules = redesignEnabled ? seatingDetailsRedesignRules : seatingDetailsRules;
    const errorStrings = redesignEnabled ? seatingDetailsRedesignErrorStrings : seatingDetailsErrorStrings;

    if (this.props.required) {
      errors = ValidationService.validate(room, rules, errorStrings);
    }

    //check number of seats to be maximum 200
    if (room.seatingRowCount * room.seatingColumnCount > 200) {
      seatsError = 'seatingChart.seatingDetails.nrOfSeatsError';
      this.setState({
        seatsError,
      });
    }

    const newErrors = Object.assign({}, this.state.errors, errors);
    this.setState({
      errors: newErrors,
    });

    if (
      (!isEmpty(newErrors) && Object.keys(newErrors).some((key) => newErrors[key])) ||
      seatsError
    ) {
      return;
    }

    if (redesignEnabled) {
      // The Seating Arrangement tile is required, editable, and never a no-op: the room's
      // seating_id must be written as one of the 2 new-model values. Work Surface fields no
      // longer exist on this popup and the spacing badge is display-only — both are stripped
      // entirely (not sent as 0/empty) so the backend (STATEDIST-11171) preserves whatever
      // spacing figure is already stored server-side instead of it being overwritten.
      if (isEmpty(seatingArrangement)) {
        return;
      }

      return {
        ...omit(room, [
          'writingSurfaceWidth',
          'writingSurfaceDepth',
          'distanceBetweenShoulders',
          'distanceBetweenHeads',
        ]),
        seating: omit(seatingArrangement, ['key']),
      };
    }

    return room;
  }

  onSelectSeatingArrangement(selected) {
    this.setState({
      seatingArrangement: selected,
      isDirty: true,
    });
  }

  renderSeatingArrangementTiles() {
    const { t } = this.props;
    const { seating } = this.state.options;
    const { seatingArrangement } = this.state;

    return seating.map((option) => {
      const spacingFt = SPACING_FT_BY_SEATING_KEY[option.key];
      return (
        <ShapeSelect
          key={option.id}
          name="seatingArrangement"
          value={option.key}
          selected={seatingArrangement.key === option.key}
          // A row /seatings returns that isn't in SPACING_FT_BY_SEATING_KEY has no known
          // spacing yet — omit the subtitle rather than interpolating an undefined count.
          subtitle={spacingFt !== undefined ? t('roomBuilder.spacingFeetLabel', { count: spacingFt }) : undefined}
          shape={SHAPE_ICON_KEYS_BY_SEATING_KEY[option.key] || []}
          label={option.name}
          onChange={() => this.onSelectSeatingArrangement(option)}
        />
      );
    });
  }

  onSeatingDetailsChange(e) {
    let { value, name } = e.target;
    let { seatsError } = this.state;
    let error;

    if (+value < 0) {
      value = 0;
    }

    if (value.length > 1 && +value[0] === 0) {
      value = value.substring(1);
    }

    if (value !== '' && +value === 0) {
      error = ['errors.zeroNotAllowed'];
    }

    if (!error) {
      if (name === 'seatingColumnCount' || name === 'seatingRowCount') {
        //check max value
        error = +value > 25 ? ['seatingChart.seatingDetails.maxNrOfSeatsError'] : '';
        seatsError = null;
      } else if (name === 'distanceBetweenShoulders' || name === 'distanceBetweenHeads') {
        if (+value < 1 && value.length <= 2) {
          error = ['seatingChart.seatingDetails.minimumFeat'];
        } else if (value.length > 2) {
          error = this.props.t('errors.fieldDigitLimitation', { limit: 2 });
        } else {
          error = '';
        }
      } else {
        //check field limit 2
        error = value.length > 2 ? this.props.t('errors.fieldDigitLimitation', { limit: 2 }) : '';
      }
    }

    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [name]: error || null,
      },
      seatsError,
      isDirty: true,
    }));
  }

  render() {
    const { room, errors, seatsError } = this.state;
    const { redesignEnabled, t } = this.props;
    const totalSeats = (room.seatingRowCount || 0) * (room.seatingColumnCount || 0);

    return (
      <div className="seating-details-container">
        {redesignEnabled && (
          <div className="seating-input-group">
            <label className="fw-bold w-100" id="seatingArrangementLabel">
              {t('roomBuilder.seatingArrangementLabel')}
            </label>
            <div
              className="shapes-container"
              role="radiogroup"
              aria-labelledby="seatingArrangementLabel">
              {this.renderSeatingArrangementTiles()}
            </div>
          </div>
        )}
        {redesignEnabled ? (
          // STATEDIST-11666: matches RoomStructure.jsx's .seat-count-section/.seat-count-box
          // design exactly — labeled Rows/Columns inputs, x and = operators, and the
          // total-seats tile all inline in one bordered box, instead of the legacy plain
          // "[input] rows of [input] seats" text row with the total tile rendered separately
          // underneath. Presentational only — the same onSeatingDetailsChange handler and
          // errors/seatsError state drive both this and the legacy branch below.
          <div className="seat-count-section">
            <label
              className={`${
                errors.seatingRowCount || errors.seatingColumnCount || seatsError ? 'label-error' : ''
              } fw-bold w-100`}>
              {this.props.t('seatingChart.seatingDetails.nrOfSeatsLabel')}
            </label>
            <div className="seat-count-box">
              <div className="seat-count-field">
                <label htmlFor="seatingRowCount">{t('roomBuilder.rowsLabel')}</label>
                <input
                  id="seatingRowCount"
                  name="seatingRowCount"
                  type="number"
                  min="0"
                  value={room.seatingRowCount || ''}
                  onChange={this.onSeatingDetailsChange}
                />
                {errors.seatingRowCount?.[0] && (
                  <span className="text-error font-size-12 seat-count-field-error">
                    {t(errors.seatingRowCount[0])}
                  </span>
                )}
              </div>
              <span className="operator">&times;</span>
              <div className="seat-count-field">
                <label htmlFor="seatingColumnCount">{t('roomBuilder.columnsLabel')}</label>
                <input
                  id="seatingColumnCount"
                  name="seatingColumnCount"
                  type="number"
                  min="0"
                  value={room.seatingColumnCount || ''}
                  onChange={this.onSeatingDetailsChange}
                />
                {errors.seatingColumnCount?.[0] && (
                  <span className="text-error font-size-12 seat-count-field-error">
                    {t(errors.seatingColumnCount[0])}
                  </span>
                )}
              </div>
              <span className="operator">=</span>
              <div className="total-seats-box">
                <strong>{totalSeats}</strong>
                <span>{t('roomBuilder.totalSeatsLabel')}</span>
              </div>
            </div>
            {seatsError && (
              <div className="text-error name-error font-size-12">{this.props.t(seatsError)}</div>
            )}
          </div>
        ) : (
          <div className="seating-input-group">
            <label
              className={`${
                errors.seatingRowCount || errors.seatingColumnCount || seatsError ? 'label-error' : ''
              } fw-bold w-100`}>
              {this.props.t('seatingChart.seatingDetails.nrOfSeatsLabel')}
            </label>
            <div className="input-section">
              <Input
                type="number"
                containerClassName={seatsError && !errors.seatingRowCount ? 'hide-error' : ''}
                name="seatingRowCount"
                id="seatingRowCount"
                value={room.seatingRowCount || ''}
                onChange={this.onSeatingDetailsChange}
                errors={errors.seatingRowCount || seatsError}
                required
              />
              <span>{this.props.t('seatingChart.seatingDetails.rowsOf')}</span>
              <Input
                type="number"
                containerClassName={seatsError && !errors.seatingColumnCount ? 'hide-error' : ''}
                name="seatingColumnCount"
                id="seatingColumnCount"
                value={room.seatingColumnCount || ''}
                onChange={this.onSeatingDetailsChange}
                errors={errors.seatingColumnCount || seatsError}
                required
              />
              <span>{this.props.t('common.labels.seats')}</span>
            </div>
            {seatsError && (
              <div className="text-error name-error font-size-12">{this.props.t(seatsError)}</div>
            )}
          </div>
        )}
        {!redesignEnabled && (
          <>
            <div className="seating-input-group">
              <label
                className={`${
                  errors.writingSurfaceWidth || errors.writingSurfaceDepth ? 'label-error' : ''
                } fw-bold w-100`}>
                {this.props.t('seatingChart.seatingDetails.writingSurfacesLabel')}
              </label>
              <div className="input-section">
                <Input
                  type="number"
                  name="writingSurfaceWidth"
                  id="writingSurfaceWidth"
                  value={room.writingSurfaceWidth || ''}
                  onChange={this.onSeatingDetailsChange}
                  errors={errors.writingSurfaceWidth}
                  required
                />
                <span>{this.props.t('seatingChart.seatingDetails.inchBy')}</span>
                <Input
                  type="number"
                  name="writingSurfaceDepth"
                  id="writingSurfaceDepth"
                  value={room.writingSurfaceDepth || ''}
                  onChange={this.onSeatingDetailsChange}
                  errors={errors.writingSurfaceDepth}
                  required
                />
                <span>{this.props.t('common.labels.inch')}</span>
              </div>
            </div>
            <div className="seating-input-group">
              <label
                className={`${errors.distanceBetweenShoulders ? 'label-error' : ''} fw-bold w-100`}>
                {this.props.t('seatingChart.seatingDetails.shoulderDistanceLabel')}
              </label>
              <div className="input-section">
                <Input
                  type="number"
                  name="distanceBetweenShoulders"
                  id="distanceBetweenShoulders"
                  value={room.distanceBetweenShoulders || ''}
                  onChange={this.onSeatingDetailsChange}
                  errors={errors.distanceBetweenShoulders}
                  required
                />
                <span>{this.props.t('common.labels.feet')}</span>
              </div>
              {!errors.distanceBetweenShoulders && (
                <p className="font-size-12 mt-1">
                  {this.props.t('seatingChart.seatingDetails.minimumFeat')}{' '}
                </p>
              )}
            </div>
            <div className="seating-input-group">
              <label className={`${errors.distanceBetweenHeads ? 'label-error' : ''} fw-bold w-100`}>
                {this.props.t('seatingChart.seatingDetails.headDistanceLabel')}
              </label>
              <div className="input-section">
                <Input
                  type="number"
                  name="distanceBetweenHeads"
                  id="distanceBetweenHeads"
                  value={room.distanceBetweenHeads || ''}
                  onChange={this.onSeatingDetailsChange}
                  errors={errors.distanceBetweenHeads}
                  required
                />
                <span>{this.props.t('common.labels.feet')}</span>
              </div>
              {!errors.distanceBetweenHeads && (
                <p className="font-size-12 mt-1">
                  {this.props.t('seatingChart.seatingDetails.minimumFeat')}{' '}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}

SeatingDetails.propTypes = {
  room: PropTypes.shape({}).isRequired,
  required: PropTypes.bool,
  redesignEnabled: PropTypes.bool,
  seatingOptions: PropTypes.arrayOf(PropTypes.shape({})),
  // STATEDIST-11666 follow-up: optional — notified with the current isDisabled() result on
  // mount and every update, so the parent can disable its own Save control accordingly.
  // Omitting it is a no-op.
  onValidityChange: PropTypes.func,
  t: PropTypes.func.isRequired,
};

SeatingDetails.defaultProps = {
  required: false,
  redesignEnabled: false,
  seatingOptions: [],
  onValidityChange: null,
};

const TranslatedSeatingDetails = withTranslation()(SeatingDetails);

// Forward ref to the wrapped component
const SeatingDetailsWithRef = forwardRef((props, ref) => (
  <TranslatedSeatingDetails {...props} forwardedRef={ref} />
));

SeatingDetailsWithRef.displayName = 'SeatingDetails';

export default SeatingDetailsWithRef;
